import { useEffect, useState } from "react";
import { Search, CircleDollarSign, Plane, User, Briefcase, Check, Loader2 } from "lucide-react";

/**
 * AdvancedSearchLoader
 * -----------------------------------------------------------------------------
 * Multi-stage "Running 5-layer match" loader for the AdvancedResults page.
 *
 * The backend match takes 800-1600ms on a warm DB; we don't have progress
 * events, so we choreograph 5 staged reveals on the client that name each
 * of the 5 scoring layers (Academic, Financial, Visa, Personal, Career).
 * This turns dead-time into transparency about what the platform is
 * actually computing, and reduces perceived latency.
 *
 * Layer names must match the copy used in the results tabs so the loader
 * educates on the vocabulary the user is about to see.
 */
const STAGES = [
  { key: "academic", label: "Academic fit", detail: "Comparing your grades against each course's typical offer", Icon: Search },
  { key: "financial", label: "Financial fit", detail: "Net cost after every accessible scholarship", Icon: CircleDollarSign },
  { key: "visa", label: "Visa pathway", detail: "Your passport vs the destination's student & work rules", Icon: Plane },
  { key: "personal", label: "Personal fit", detail: "City weather, halal food, class-size, campus type", Icon: User },
  { key: "career", label: "Career alignment", detail: "Graduate employers your target industry hires from", Icon: Briefcase },
];

export default function AdvancedSearchLoader({ compact = false }) {
  const [stageIx, setStageIx] = useState(0);

  useEffect(() => {
    // Stagger reveals every 320ms so the whole sequence completes in ~1.6s.
    // If the network is slower than that, the last stage stays pulsing until
    // the parent unmounts the loader — no "stuck" appearance.
    if (stageIx >= STAGES.length - 1) return;
    const t = setTimeout(() => setStageIx((s) => s + 1), 320);
    return () => clearTimeout(t);
  }, [stageIx]);

  if (compact) {
    // Compact horizontal variant — used above the skeleton grid so the user
    // sees "we're computing X" without the choreography taking over the fold.
    return (
      <div
        className="rounded-md border border-stone-200 bg-white px-4 py-3"
        data-testid="advanced-search-loader-compact"
        role="status"
        aria-live="polite"
      >
        <div className="flex items-center gap-2 text-[#4C1D95]">
          <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
          <div className="label-uppercase !text-[10px] text-[#4C1D95]">
            Running 5-layer match — {STAGES[Math.min(stageIx, STAGES.length - 1)].label}
          </div>
        </div>
        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
          {STAGES.map((s, i) => {
            const done = i < stageIx;
            const active = i === stageIx;
            return (
              <div
                key={s.key}
                data-testid={`loader-stage-${s.key}`}
                data-active={active ? "true" : "false"}
                data-done={done ? "true" : "false"}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-underground-sc tracking-wider transition-colors ${
                  done
                    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                    : active
                    ? "border-[#4C1D95]/40 bg-[#F5F0FF] text-[#4C1D95]"
                    : "border-stone-200 bg-white text-stone-400"
                }`}
              >
                {done ? <Check className="h-2.5 w-2.5" /> : active ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <s.Icon className="h-2.5 w-2.5" />}
                {s.label}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-[70vh] flex items-center justify-center px-6 py-10"
      data-testid="advanced-search-loader"
      role="status"
      aria-live="polite"
    >
      <div className="w-full max-w-2xl">
        <div className="flex items-center gap-2 text-[#4C1D95]">
          <Loader2 className="h-4 w-4 animate-spin" />
          <div className="label-uppercase !text-[10px] text-[#4C1D95]">
            Running 5-layer match
          </div>
        </div>
        <h2 className="font-editorial text-2xl sm:text-3xl mt-2 leading-tight">
          Scoring every course against your profile.
        </h2>
        <p className="text-sm text-stone-600 mt-2 max-w-lg">
          We're checking your grades, budget, passport and career goals against
          the full catalogue. Usually takes 1–2 seconds.
        </p>

        <ol className="mt-8 space-y-3">
          {STAGES.map((s, i) => {
            const done = i < stageIx;
            const active = i === stageIx;
            return (
              <li
                key={s.key}
                data-testid={`loader-stage-${s.key}`}
                data-active={active ? "true" : "false"}
                data-done={done ? "true" : "false"}
                className={`flex items-start gap-3 rounded-md border px-3.5 py-2.5 transition-all duration-300 ${
                  done
                    ? "border-emerald-200 bg-emerald-50/40"
                    : active
                    ? "border-[#4C1D95]/40 bg-[#F5F0FF] shadow-sm"
                    : "border-stone-200 bg-white opacity-60"
                }`}
              >
                <div
                  className={`h-6 w-6 rounded-full flex items-center justify-center shrink-0 ${
                    done
                      ? "bg-emerald-600 text-white"
                      : active
                      ? "bg-[#4C1D95] text-white"
                      : "bg-stone-200 text-stone-500"
                  }`}
                >
                  {done ? (
                    <Check className="h-3.5 w-3.5" />
                  ) : active ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <s.Icon className="h-3.5 w-3.5" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div
                    className={`text-sm font-medium ${
                      done ? "text-emerald-800" : active ? "text-stone-900" : "text-stone-600"
                    }`}
                  >
                    Layer {i + 1} · {s.label}
                  </div>
                  <div className="text-[11px] text-stone-500 mt-0.5 leading-snug">
                    {s.detail}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
